import { computed, Ref, ref } from "vue";
import { ProfileAdministrationAPI, ProfileContractorAPI } from "../../api";
import { Participant, ParticipantStatus } from "../../types/PersologOnlineTypes";
import { useI18n } from "vue-i18n";

export default function useDeleteParticipant(isDirectLink: boolean, participant: Ref<Participant>) {
    const { t } = useI18n();

    const loading = ref(false);
    // Labels of the generated group charts the participant is in: their PDFs go with the participant (RDT-475).
    const affectedGroupCharts = ref<string[]>([]);

    /** Looks up the group charts with a PDF that the participant is in, so the confirmation can name them. */
    async function loadAffectedGroupCharts() {
        affectedGroupCharts.value = [];
        try {
            const charts = await ProfileContractorAPI.getGroupChartsForProject(participant.value.projectId);
            const participantId = participant.value.id as number;
            affectedGroupCharts.value = charts
                .filter((chart) => chart.languages.length > 0 && chart.participantIds.includes(participantId))
                .map((chart) => chart.label);
        } catch (error) {
            // The warning is a courtesy: without it the participant can still be deleted.
            console.error("Error loading the participant's group charts:", error);
        }
    }

    const deletionMessage = computed((): { header: string; message: string } => {
        const status = participant.value.status;

        if (status === undefined) {
            return {
                header: t("project-details.participants.delete.profile.heading"),
                message: t("project-details.participants.delete.profile.warning"),
            };
        }

        // Direct link participants past the questionnaire start are pseudo-deleted by the backend:
        // the entry stays and the used contingent slot remains consumed, so the warning must say so.
        if (
            isDirectLink &&
            ![ParticipantStatus.CREATED, ParticipantStatus.ACTIVATED, ParticipantStatus.STARTED].includes(status)
        ) {
            return {
                header: t("project-details.participants.delete.heading"),
                message: t("project-details.participants.delete.direct-link.warning"),
            };
        }

        if ([ParticipantStatus.CREATED, ParticipantStatus.REVOKED].includes(status)) {
            return {
                header: t("project-details.participants.delete.heading"),
                message: t("project-details.participants.delete.warning"),
            };
        } else if ([ParticipantStatus.ACTIVATED, ParticipantStatus.STARTED].includes(status)) {
            return {
                header: t("project-details.participants.delete.questionnaire.heading"),
                message: t("project-details.participants.delete.questionnaire.warning"),
            };
        } else if ([ParticipantStatus.DELETED].includes(status)) {
            return {
                header: t("project-details.participants.delete.profile.heading-for-already-deleted"),
                message: t("project-details.participants.delete.profile.warning-for-already-deleted"),
            };
        } else {
            return {
                header: t("project-details.participants.delete.profile.heading"),
                message: t("project-details.participants.delete.profile.warning"),
            };
        }
    });

    // profile-administration has profile-contractor delete all of the participant's data (answers,
    // calculations, direct input, profiles) before it deletes the participant itself (RDT-470).
    async function deleteParticipant() {
        loading.value = true;
        try {
            await ProfileAdministrationAPI.deleteParticipant(participant.value.id as number);
        } finally {
            loading.value = false;
        }
    }

    return {
        affectedGroupCharts,
        deleteParticipant,
        deletionMessage,
        loadAffectedGroupCharts,
        loading,
    };
}
